import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { getBackendUrl } from "../config";
import { buildEnergyMeterDetailPath } from '../utils/energyMeterBrandDisplay';

const BACKEND_URL = getBackendUrl();
// Fix Leaflet default icon (webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const KOSOVO_CENTER = [42.6026, 20.9030];

/** Ekzekuton lista funksionesh async me paralelizëm të kufizuar (shmang timeout nga shumë kërkesa njëkohësisht). */
async function runPool(limit, factories) {
  if (!Array.isArray(factories) || factories.length === 0) return [];
  const results = new Array(factories.length);
  let next = 0;
  const worker = async () => {
    for (;;) {
      const idx = next++;
      if (idx >= factories.length) break;
      try {
        results[idx] = await factories[idx]();
      } catch {
        results[idx] = null;
      }
    }
  };
  const workers = Math.min(Math.max(1, limit), factories.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

const formatLocalYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function MapCenterController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center) && center.length >= 2) {
      map.flyTo(center, 12);
    }
  }, [map, center]);
  return null;
}

const Kosova = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  /** null = ende kontrollohet; true/false = përgjigjja e /api/kosova/health (opsionale, nuk bllokon faqen). */
  const [liveHubOk, setLiveHubOk] = useState(null);
  const [mainLoading, setMainLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [dataLoggers, setDataLoggers] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyStats, setCompanyStats] = useState({});

  const getToken = () => localStorage.getItem('access_token');
  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/kosova/health`, { timeout: 8000 });
        if (!cancelled) setLiveHubOk(r.data?.ok === true);
      } catch {
        if (!cancelled) setLiveHubOk(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchMain = async () => {
      setMainLoading(true);
      const req = { headers: headers(), timeout: 30000 };
      try {
        const [compRes, dlRes, invRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/companies`, req),
          axios.get(`${BACKEND_URL}/api/devices/data-loggers`, req),
          axios.get(`${BACKEND_URL}/api/devices/inverters`, req),
        ]);
        setCompanies(Array.isArray(compRes.data) ? compRes.data : []);
        setDataLoggers(Array.isArray(dlRes.data) ? dlRes.data : []);
        setInverters(Array.isArray(invRes.data) ? invRes.data : []);
      } catch {
        setCompanies([]);
        setDataLoggers([]);
        setInverters([]);
      } finally {
        setMainLoading(false);
      }
    };
    fetchMain();
  }, [headers]);

  useEffect(() => {
    if (!companies?.length && !dataLoggers?.length) return;
    const ac = new AbortController();
    const { signal } = ac;

    const ensure = (statsObj, cid) => {
      if (!cid) return;
      const key = String(cid);
      if (!statsObj[key]) {
        statsObj[key] = {
          activePowerMW: 0,
          todayMWh: 0,
          totalMWh: 0,
          pvNowMw: 0,
          emTodayMWh: 0,
          hasEnergyMeters: false,
          energyMeterDetailTo: null,
        };
      }
    };

    const fetchStats = async () => {
      const stats = {};
      const hdr = headers();
      const ax = { headers: hdr, timeout: 28000, signal };

      const now = new Date();
      const todayStr = formatLocalYmd(now);
      const nowTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      const todayFrom = `${todayStr}T00:00:00`;
      const todayTo = `${todayStr}T${nowTime}`;
      const yday = new Date(now);
      yday.setDate(yday.getDate() - 1);
      const yesterdayStr = formatLocalYmd(yday);

      const dlIds = (dataLoggers || []).filter((dl) => dl.company_id).map((dl) => dl.id).filter(Boolean);
      if (dlIds.length > 0 && !signal.aborted) {
        try {
          const dlRes = await axios.post(
            `${BACKEND_URL}/api/dashboard/datalogger-company-real-stats-batch`,
            { data_logger_ids: dlIds, yesterday_date: yesterdayStr },
            ax
          );
          const byCo = dlRes.data?.stats_by_company || {};
          for (const cid of Object.keys(byCo)) {
            const row = byCo[cid];
            ensure(stats, cid);
            stats[cid].activePowerMW = Number(row.activePowerMW) || 0;
            stats[cid].todayMWh = Number(row.todayMWh) || 0;
            stats[cid].totalMWh = Number(row.totalMWh) || 0;
          }
        } catch {
          /* DL stats mbeten në default */
        }
      }

      const companyFactories = (companies || []).map((c) => async () => {
        const cid = String(c.id);
        try {
          const qs = new URLSearchParams();
          qs.set('today_from', todayFrom);
          qs.set('today_to', todayTo);
          qs.set('yesterday_from', `${yesterdayStr}T00:00:00`);
          qs.set('yesterday_to', `${yesterdayStr}T23:59:59`);

          const [emStatsRes, metersRes] = await Promise.all([
            axios.get(
              `${BACKEND_URL}/api/companies/${encodeURIComponent(cid)}/dashboard-energy-meter-stats?${qs.toString()}`,
              ax
            ),
            axios.get(`${BACKEND_URL}/api/devices/energy-meters?company_id=${encodeURIComponent(cid)}`, ax),
          ]);

          const d = emStatsRes.data || {};
          const meters = Array.isArray(metersRes.data) ? metersRes.data : [];
          const primaryMeter = meters.find((m) => m?.id);
          const energyMeterDetailTo = primaryMeter ? buildEnergyMeterDetailPath(primaryMeter) : null;

          return {
            cid,
            hasEnergyMeters: meters.length > 0,
            energyMeterDetailTo,
            pvNowMw: Number(d.pv_now_mw) || 0,
            emTodayMWh: Number(d.pv_today_mwh) || 0,
          };
        } catch {
          return {
            cid,
            hasEnergyMeters: false,
            energyMeterDetailTo: null,
            pvNowMw: 0,
            emTodayMWh: 0,
          };
        }
      });

      const companyParts = await runPool(3, companyFactories);
      for (const p of companyParts) {
        if (!p) continue;
        ensure(stats, p.cid);
        stats[p.cid].hasEnergyMeters = p.hasEnergyMeters;
        stats[p.cid].energyMeterDetailTo = p.energyMeterDetailTo;
        stats[p.cid].pvNowMw = p.pvNowMw;
        stats[p.cid].emTodayMWh = p.emTodayMWh;
      }

      if (!signal.aborted) setCompanyStats(stats);
    };

    fetchStats();
    const id = setInterval(fetchStats, 60000);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [companies, dataLoggers, headers]);

  /** Kapaciteti në kW: për çdo data logger të kompanisë = shuma e Fuqia (kW) të të gjithë inverterëve të atij logger-i;
   * nëse s’ka inverterë me vlerë, përdoret installed_capacity_kw i atij DL. Shuma e të gjitha DL-ve (shumë loggera = mbledhje). */
  const getCompanyCapacity = (companyId) => {
    const idStr = (x) => (x == null || x === '' ? '' : String(x));
    const cid = idStr(companyId);
    const companyDLs = dataLoggers.filter((dl) => idStr(dl.company_id) === cid);
    let totalKw = 0;
    for (const dl of companyDLs) {
      const dlid = idStr(dl.id);
      const invKw = inverters
        .filter((inv) => idStr(inv.data_logger_id) === dlid)
        .reduce((s, inv) => s + (Number(inv.power_kw) || Number(inv.rated_power_kw) || 0), 0);
      const dlFallback = Number(dl.installed_capacity_kw) || 0;
      totalKw += invKw > 0 ? invKw : dlFallback;
    }
    return Number.isFinite(totalKw) ? totalKw : 0;
  };

  const filteredCompanies = useMemo(() => {
    const q = (searchQuery || '').toLowerCase().trim();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q)
    );
  }, [companies, searchQuery]);

  const companiesWithLocation = useMemo(() => {
    const toNum = (v) => {
      if (v == null || v === '') return NaN;
      const n = Number(String(v).trim().replace(/,/g, '.'));
      return Number.isFinite(n) ? n : NaN;
    };
    return filteredCompanies.filter((c) => {
      const lat = toNum(c.latitude);
      const lon = toNum(c.longitude);
      return !Number.isNaN(lat) && !Number.isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
    });
  }, [filteredCompanies]);

  const totalCapacity = companies.reduce((s, c) => s + getCompanyCapacity(c.id), 0) / 1000;
  const totalPvNowMw = Object.values(companyStats).reduce((s, st) => s + (Number(st?.pvNowMw) || 0), 0);
  const totalEmTodayMwh = Object.values(companyStats).reduce(
    (s, st) => s + (st.hasEnergyMeters ? (Number(st.emTodayMWh) || 0) : 0),
    0
  );

  const getSt = (companyId) => companyStats[String(companyId)] || companyStats[companyId] || {};

  /** Gjithmonë hap EnergyMeterDetail (`/devices/.../energy-meter/...`), jo listën `?tab=ems`. */
  const navigateToCompanyEnergyMeterDetail = async (companyId) => {
    const cid = companyId == null ? '' : String(companyId).trim();
    if (!cid) return;

    const st = getSt(companyId);
    if (st.energyMeterDetailTo) {
      navigate(st.energyMeterDetailTo);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/devices/energy-meters?company_id=${encodeURIComponent(cid)}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 25000 },
      );
      const meters = Array.isArray(res.data) ? res.data : [];
      const primary = meters.find((m) => m?.id);
      const path = primary ? buildEnergyMeterDetailPath(primary) : null;
      if (path) {
        navigate(path);
        setCompanyStats((prev) => {
          const key = cid;
          const cur = prev[key] || {};
          return {
            ...prev,
            [key]: { ...cur, energyMeterDetailTo: path, hasEnergyMeters: meters.length > 0 },
          };
        });
        return;
      }
      toast.error('Nuk u gjet njehsor energjie për këtë kompani.');
    } catch {
      toast.error('Nuk u ngarkuan njehsorët e energjisë.');
    }
  };
  const todayMwhForCompany = (companyId) => {
    const st = getSt(companyId);
    if (st.hasEnergyMeters) return Number(st.emTodayMWh) || 0;
    return Number(st.todayMWh) || 0;
  };

  const formatVal = (v, decimals = 2) => (v == null || !Number.isFinite(v) ? '0.00' : Number(v).toFixed(decimals));

  const flyToCenter = useMemo(() => {
    if (selectedCompany?.latitude != null && selectedCompany?.longitude != null) {
      return [Number(selectedCompany.latitude), Number(selectedCompany.longitude)];
    }
    return null;
  }, [selectedCompany]);

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const mapTileUrl = isDark
    ? 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
  const mapBgColor = isDark ? '#475569' : '#f8fafc';

  return (
    <Layout>
      <div className="kosova-dashboard flex flex-col md:h-[calc(100vh-4rem)] bg-gray-100 dark:bg-slate-900 overflow-x-hidden">
        {/* Header – mobile: vetëm kërko; desktop: titull + datë + kërko */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-3 py-2 md:px-4 border-b border-gray-300 dark:border-slate-700 bg-gray-200 dark:bg-slate-800/50 shrink-0">
          <div className="flex items-center justify-between">
            <h1 className="text-base md:text-lg font-bold text-gray-900 dark:text-white truncate">Sistemi i Ruajtjes së Energjisë Solare</h1>
            <span className="md:hidden text-xs text-gray-500 dark:text-slate-400 shrink-0 ml-2">
              {new Date().toLocaleDateString('sq-AL', { day: 'numeric', month: 'short' })}
            </span>
          </div>
          <div className="relative w-full md:w-auto">
            <input
              type="text"
              placeholder="Kërko sipas lokacionit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-2 md:py-1.5 rounded-lg bg-gray-200 dark:bg-slate-700 border border-gray-400 dark:border-slate-600 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-slate-400 w-full md:w-48 focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-slate-400">🔍</span>
          </div>
          <span className="hidden md:inline text-sm text-gray-500 dark:text-slate-400">
            {new Date().toLocaleDateString('sq-AL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {/* Mobile: emrat e kompanive kur kërkon */}
        <div className="md:hidden shrink-0">
          {searchQuery.trim() && (
            <div className="scroll-dark max-h-36 overflow-y-auto border-b border-gray-300 dark:border-slate-700 bg-gray-200 dark:bg-slate-800/50">
              {filteredCompanies.length === 0 ? (
                <p className="px-3 py-2 text-gray-500 dark:text-slate-500 text-sm">Nuk u gjet asnjë kompani</p>
              ) : (
                <ul className="py-1">
                  {filteredCompanies.map((c) => (
                    <li key={c.id} className="flex border-b border-gray-300/60 dark:border-slate-600/50 last:border-0">
                      <button
                        type="button"
                        onClick={() => navigateToCompanyEnergyMeterDetail(c.id)}
                        className={`flex-1 min-w-0 text-left px-3 py-2.5 text-sm transition cursor-pointer ${selectedCompany?.id === c.id ? 'bg-gray-400 dark:bg-slate-600 text-white' : 'text-gray-800 dark:text-slate-200 hover:bg-gray-300 dark:hover:bg-slate-700'}`}
                      >
                        {c.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedCompany(c)}
                        className="shrink-0 px-2.5 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-300/80 dark:hover:bg-slate-600/80 border-l border-gray-300/60 dark:border-slate-600/50"
                        title="Harta"
                        aria-label="Harta"
                      >
                        📍
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {mainLoading && (
          <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-slate-400">{t('checkingConnection')}</div>
        )}

        {!mainLoading && liveHubOk === false && (
          <div className="px-4 py-2.5 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-sm shrink-0 leading-snug">
            {t('kosovaRegionalLinkBanner')}
          </div>
        )}

        {!mainLoading && (
          <>
            {/* Desktop: 3 kolona */}
            <div className="hidden md:flex flex-1 min-h-0 min-w-0 overflow-hidden">
              {/* Left panel */}
              <div className="scroll-dark w-72 shrink-0 flex flex-col border-r border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800/30 overflow-y-auto">
                <div className="p-4 border-b border-gray-300 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase mb-1">Energjia e prodhuar sot</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatVal(totalEmTodayMwh, 3)} MWh</p>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Totali nga të gjithë energy meterët</p>
                </div>
                <div className="p-4 border-b border-gray-300 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase mb-2">Tendenca mujore</p>
                  <div className="flex items-center justify-center h-24 bg-gray-100 dark:bg-slate-700/50 rounded-lg text-gray-500 dark:text-slate-500 text-sm">Nuk ka të dhëna</div>
                </div>
                <div className="p-4 flex-1">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase mb-2">Prodhimi sipas kompanisë</p>
                  <div className="space-y-2">
                    {filteredCompanies.length === 0 ? (
                      <p className="text-gray-500 dark:text-slate-500 text-sm">Nuk ka të dhëna</p>
                    ) : (
                      filteredCompanies.map((c) => {
                        const cap = getCompanyCapacity(c.id);
                        const st = getSt(c.id);
                        const pvNow = Number(st.pvNowMw) || 0;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => navigateToCompanyEnergyMeterDetail(c.id)}
                            title={c.name}
                            className="flex w-full min-w-0 justify-between items-start gap-2 rounded bg-gray-100 dark:bg-slate-700/50 hover:bg-gray-200 dark:hover:bg-slate-600/60 transition-colors py-1.5 px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-purple-500 cursor-pointer text-left"
                          >
                            <span className="text-gray-900 dark:text-white truncate flex-1 min-w-0">{c.name}</span>
                            <div className="flex flex-col items-end shrink-0">
                              <span className="text-green-600 dark:text-green-400 font-semibold tabular-nums">{formatVal(pvNow)} MW</span>
                              <span className="text-gray-500 dark:text-slate-400 text-[10px] tabular-nums">{formatVal(cap / 1000, 1)} MW kap.</span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Hartë – 100% responsive, vetëm scroll lart-poshtë */}
              <div className="flex-1 min-w-0 w-full relative overflow-hidden">
                <MapContainer
                  center={KOSOVO_CENTER}
                  zoom={8}
                  className="w-full h-full"
                  style={{ background: mapBgColor, minHeight: 200 }}
                  zoomControl={true}
                  scrollWheelZoom={true}
                >
                  {flyToCenter && <MapCenterController center={flyToCenter} />}
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url={mapTileUrl}
                  />
                  {companiesWithLocation.map((c) => (
                    <Marker
                      key={c.id}
                      position={[Number(c.latitude), Number(c.longitude)]}
                      eventHandlers={{
                        click: () => { },
                      }}
                    >
                      <Popup>
                        <div className="min-w-[180px]">
                          <p className="font-semibold text-gray-900">{c.name}</p>
                          {c.address && <p className="text-xs text-gray-600">{c.address}</p>}
                          <p className="text-sm mt-2 text-green-600">
                            Kapaciteti: {formatVal(getCompanyCapacity(c.id) / 1000, 2)} MW
                          </p>
                          <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                            PV tani: {formatVal(getSt(c.id).pvNowMw)} MW
                          </p>
                          <p className="text-sm text-blue-600">
                            Prodhimi sot: {formatVal(todayMwhForCompany(c.id))} MWh
                          </p>
                          <p className="mt-2">
                            <button
                              type="button"
                              onClick={() => navigateToCompanyEnergyMeterDetail(c.id)}
                              className="text-sm font-medium text-purple-600 hover:underline dark:text-purple-400 cursor-pointer bg-transparent border-0 p-0"
                            >
                              {t('energyMeters')} →
                            </button>
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>

            {/* Mobile: harta + detajet poshtë, faqja tërësore scrollon */}
            <div className="md:hidden flex flex-col shrink-0">
              <div className="h-72 shrink-0 relative overflow-hidden">
                <MapContainer
                  center={KOSOVO_CENTER}
                  zoom={8}
                  className="w-full h-full"
                  style={{ background: mapBgColor }}
                  zoomControl={true}
                  scrollWheelZoom={true}
                >
                  {flyToCenter && <MapCenterController center={flyToCenter} />}
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url={mapTileUrl}
                  />
                  {companiesWithLocation.map((c) => (
                    <Marker key={c.id} position={[Number(c.latitude), Number(c.longitude)]}>
                      <Popup>
                        <div className="min-w-[180px]">
                          <p className="font-semibold text-gray-900">{c.name}</p>
                          {c.address && <p className="text-xs text-gray-600">{c.address}</p>}
                          <p className="text-sm mt-2 text-green-600">Kapaciteti: {formatVal(getCompanyCapacity(c.id) / 1000, 2)} MW</p>
                          <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">PV tani: {formatVal(getSt(c.id).pvNowMw)} MW</p>
                          <p className="text-sm text-blue-600">Prodhimi sot: {formatVal(todayMwhForCompany(c.id))} MWh</p>
                          <p className="mt-2">
                            <button
                              type="button"
                              onClick={() => navigateToCompanyEnergyMeterDetail(c.id)}
                              className="text-sm font-medium text-purple-600 hover:underline dark:text-purple-400 cursor-pointer bg-transparent border-0 p-0"
                            >
                              {t('energyMeters')} →
                            </button>
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <div className="scroll-dark border-t border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800/30">
                <div className="p-4 border-b border-gray-300 dark:border-slate-700">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 dark:text-slate-400 uppercase mb-1">Energjia e prodhuar sot</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400 tabular-nums">{formatVal(totalEmTodayMwh, 2)} MWh</p>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 leading-tight">Nga të gjithë energy meterët</p>
                    </div>
                    <div className="min-w-0 border-l border-gray-300 dark:border-slate-600 pl-3">
                      <p className="text-xs text-gray-500 dark:text-slate-400 uppercase mb-1">Duke prodhuar</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400 tabular-nums">{formatVal(totalPvNowMw, 2)} MW</p>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1 leading-tight">PV tani — shuma e të gjitha kompanive</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-b border-gray-300 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase mb-2">Prodhimi sipas kompanisë</p>
                  <div className="space-y-2">
                    {filteredCompanies.length === 0 ? (
                      <p className="text-gray-500 dark:text-slate-500 text-sm">Nuk ka të dhëna</p>
                    ) : (
                      filteredCompanies.map((c) => {
                        const cap = getCompanyCapacity(c.id);
                        const st = getSt(c.id);
                        const pvNow = Number(st.pvNowMw) || 0;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => navigateToCompanyEnergyMeterDetail(c.id)}
                            title={c.name}
                            className="flex w-full min-w-0 justify-between items-start gap-3 rounded bg-gray-100 dark:bg-slate-700/50 hover:bg-gray-200 dark:hover:bg-slate-600/60 transition-colors py-2 px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-purple-500 active:opacity-90 cursor-pointer text-left"
                          >
                            <span className="text-gray-900 dark:text-white truncate flex-1 min-w-0">{c.name}</span>
                            <div className="flex flex-col items-end shrink-0">
                              <span className="text-green-600 dark:text-green-400 font-semibold tabular-nums">{formatVal(pvNow)} MW</span>
                              <span className="text-gray-500 dark:text-slate-400 text-[10px] tabular-nums">{formatVal(cap / 1000, 1)} MW kap.</span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Bottom bar – fshehur në mobile */}
        <div className="hidden md:flex items-center justify-around py-3 px-4 bg-gray-200 dark:bg-slate-800 border-t border-gray-300 dark:border-slate-700 shrink-0">
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-slate-400">Kompani totale</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{companies.length}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-slate-400">Kapaciteti total</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{formatVal(totalCapacity)} MW</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-slate-400">PV tani (total)</p>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatVal(totalPvNowMw)} MW</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-slate-400">Energjia prodhuar</p>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatVal(totalEmTodayMwh, 3)} MWh</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Kosova;
